/**
 * Hook: useSearchFilter
 *
 * 検索クエリのローカル状態・デバウンス・URL同期を一元管理する。
 * WorksList / EventsList で共通のパターンを抽出したもの。
 */

import { useState, useEffect, useRef } from 'react';
import { useDebounce } from './useDebounce';

/**
 * 検索フィルタの状態とデバウンスを管理する
 *
 * @param storeSearchQuery - URL/ストア側の検索クエリ（外部ソース）
 * @param onUpdate - デバウンス確定時に呼ばれるコールバック
 * @returns localQuery（入力値）と setLocalQuery（入力値の更新）
 */
export function useSearchFilter(
  storeSearchQuery: string,
  onUpdate: (query: string) => void
): { localQuery: string; setLocalQuery: (query: string) => void } {
  const [localQuery, setLocalQuery] = useState(storeSearchQuery);
  const debouncedQuery = useDebounce(localQuery, 300);

  // onUpdate を ref で安定化（依存配列から除外するため）
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  // 外部ソース → ローカル同期
  useEffect(() => {
    setLocalQuery(storeSearchQuery);
  }, [storeSearchQuery]);

  // ローカル（デバウンス後）→ 外部ソース同期
  useEffect(() => {
    if (debouncedQuery !== localQuery) return;
    if (debouncedQuery === storeSearchQuery) return;
    onUpdateRef.current(debouncedQuery);
  }, [debouncedQuery, localQuery, storeSearchQuery]);

  return { localQuery, setLocalQuery };
}
